// src/pages/LoginPage.jsx
import { useState } from "react";
import { API_BASE_URL, STORAGE_PREFIX } from "@config";
import { useNavigate } from "react-router-dom";
import { invalidateMeCache } from "../../../shared/common/useMe";

function Alert({ type = "error", title, message }) {
  const styles =
    type === "warning"
      ? {
          wrap: "bg-amber-50 border-amber-200 text-amber-900",
          icon: "text-amber-600",
          title: "text-amber-900",
          msg: "text-amber-800",
        }
      : {
          wrap: "bg-red-50 border-red-200 text-red-900",
          icon: "text-red-600",
          title: "text-red-900",
          msg: "text-red-800",
        };

  return (
    <div className={`mb-4 rounded-lg border p-4 ${styles.wrap}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${styles.icon}`} aria-hidden="true">
          {type === "warning" ? (
            // warning icon
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2 1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v5h2V10z" />
            </svg>
          ) : (
            // error icon
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 14h-2v2h2v-2zm0-10h-2v8h2V6z" />
            </svg>
          )}
        </div>

        <div className="flex-1">
          {title && <p className={`font-semibold ${styles.title}`}>{title}</p>}
          {message && <p className={`mt-1 text-sm ${styles.msg}`}>{message}</p>}
        </div>
      </div>
    </div>
  );
}

function LoginPage({ setToken, setClubId }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");

  // { type, title, message }
  const [feedback, setFeedback] = useState(null);

  const navigate = useNavigate();
  const k = (name) => `${STORAGE_PREFIX}${name}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    try {
      const response = await fetch(`${API_BASE_URL}/trainer/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.access_token) {
        localStorage.setItem(k("token"), data.access_token);
        localStorage.setItem(k("refresh_token"), data.refresh_token);
        localStorage.setItem(k("user_id"), data.user_id);
        localStorage.setItem(k("club_id"), data.club_id);
        localStorage.setItem(k("app"), "trainer");

        invalidateMeCache();

        setToken(data.access_token);
        setClubId(data.club_id);

        navigate("/overview");
        return;
      }

      // --- Feedback UX ---
      if (response.status === 429) {
        setFeedback({
          type: "warning",
          title: data.error || "Too many attempts",
          message:
            data.reason ||
            "Too many login attempts. Please wait a few minutes before trying again.",
        });
      } else if (response.status === 403) {
        // ex: { error: "Subscription required", reason: "..." }
        setFeedback({
          type: "warning",
          title: data.error || "Access denied",
          message:
            data.reason ||
            "Your account does not have the necessary rights to access Trainer.",
        });
      } else if (response.status === 401) {
        setFeedback({
          type: "error",
          title: "Incorrect credentials",
          message: "Check your login and password, then try again.",
        });
      } else if (response.status === 400) {
        setFeedback({
          type: "error",
          title: "Missing fields",
          message: data.error || "Please enter a login and password.",
        });
      } else {
        setFeedback({
          type: "error",
          title: "Connection error",
          message: data.error || "An error occurred. Please try again later.",
        });
      }
    } catch (err) {
    
      setFeedback({
        type: "error",
        title: "Connection failed",
        message: "Network problem or server unavailable. Please try again later.",
      });
    }
  };

  return (
    <div className="flex justify-center items-center h-screen w-screen bg-gray-800 z-10">
      <div className="bg-white p-8 rounded-lg shadow-lg w-96">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src="/logo-light.png" alt="Logo" className="h-50 w-50" />
        </div>

        {/* Feedback */}
        {feedback && (
          <Alert type={feedback.type} title={feedback.title} message={feedback.message} />
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Login</label>
            <input
              type="text"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              className="w-full p-2 border text-gray-700 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              autoComplete="username"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border text-gray-700 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
          >
            Login
          </button>
        </form>

        <p className="mt-4 text-xs text-gray-500 text-center">
          Need access to Trainer? Contact your administrator or support.
        </p>
      </div>
    </div>
  );
}

export default LoginPage;